import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Shuffle, Users, RefreshCw, Plus, Minus, Trophy, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

const TEAM_NAME_CATEGORIES = {
  sporty: {
    label: 'Sporty',
    names: ['Thunder', 'Lightning', 'Hurricanes', 'Titans', 'Cyclones', 'Wildcats', 'Falcons', 'Blazers', 'Sharks', 'Panthers'],
  },
  music: {
    label: 'Music',
    names: ['The Beats', 'Harmony', 'Crescendo', 'Tempo', 'Melody', 'Rhythm', 'Encore', 'Cadence', 'Allegro', 'Forte'],
  },
  film_tv: {
    label: 'Film and TV',
    names: ['The Directors', 'Scene Stealers', 'The Producers', 'Box Office', 'Red Carpet', 'The Editors', 'Blockbusters', 'The Writers', 'Front Row', 'Spotlight'],
  },
  science: {
    label: 'Science',
    names: ['Atoms', 'Photons', 'Neurons', 'Gravity', 'Cosmos', 'Quarks', 'Nebula', 'Eclipse', 'Prism', 'Helix'],
  },
  nature: {
    label: 'Nature',
    names: ['Rainforest', 'Ocean', 'Mountain', 'Aurora', 'Coral Reef', 'Glacier', 'Savanna', 'Volcano', 'Riverstone', 'Tundra'],
  },
  colours: {
    label: 'Colours',
    names: ['Crimson', 'Sapphire', 'Emerald', 'Gold', 'Violet', 'Silver', 'Coral', 'Indigo', 'Amber', 'Jade'],
  },
  animals: {
    label: 'Animals',
    names: ['Eagles', 'Dolphins', 'Lions', 'Wolves', 'Foxes', 'Owls', 'Bears', 'Hawks', 'Otters', 'Stags'],
  },
};

const TEAM_COLORS = [
  { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', badge: 'bg-blue-100', accent: 'text-blue-600' },
  { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', badge: 'bg-emerald-100', accent: 'text-emerald-600' },
  { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', badge: 'bg-amber-100', accent: 'text-amber-600' },
  { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-800', badge: 'bg-rose-100', accent: 'text-rose-600' },
  { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-800', badge: 'bg-violet-100', accent: 'text-violet-600' },
  { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-800', badge: 'bg-cyan-100', accent: 'text-cyan-600' },
  { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800', badge: 'bg-orange-100', accent: 'text-orange-600' },
  { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-800', badge: 'bg-teal-100', accent: 'text-teal-600' },
  { bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-800', badge: 'bg-pink-100', accent: 'text-pink-600' },
  { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-indigo-800', badge: 'bg-teal-100', accent: 'text-teal-600' },
];

export const TeamsPage = () => {
  const { classId } = useParams();
  const [classInfo, setClassInfo] = useState(null);
  const [students, setStudents] = useState([]);
  const [teamCount, setTeamCount] = useState(2);
  const [category, setCategory] = useState('sporty');
  const [teams, setTeams] = useState([]); // [{name, members: [], points: 0}]
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [winner, setWinner] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [studentsRes, classesRes] = await Promise.all([
          fetch(`${API}/students/${classId}`, { credentials: 'include' }),
          fetch(`${API}/classes`, { credentials: 'include' })
        ]);
        if (studentsRes.ok) setStudents(await studentsRes.json());
        if (classesRes.ok) {
          const classes = await classesRes.json();
          setClassInfo(classes.find(c => c.class_id === classId));
        }
      } catch (error) { console.error('Failed to fetch data:', error); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [classId]);

  const handleGenerateTeams = async () => {
    if (teamCount < 2) { toast.error('Need at least 2 teams'); return; }
    if (students.length < teamCount) { toast.error('Not enough students for that many teams'); return; }
    setGenerating(true);
    setWinner(null);
    try {
      const r = await fetch(`${API}/teams`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ class_id: classId, team_count: teamCount })
      });
      if (r.ok) {
        const data = await r.json();
        const catNames = [...TEAM_NAME_CATEGORIES[category].names];
        // Shuffle names
        for (let i = catNames.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [catNames[i], catNames[j]] = [catNames[j], catNames[i]];
        }
        const enriched = data.teams.map((team, idx) => ({
          name: catNames[idx] || `Team ${idx + 1}`,
          members: team.members,
          points: 0,
        }));
        setTeams(enriched);
        toast.success('Teams generated!');
      }
    } catch (error) { toast.error('Failed to generate teams'); }
    finally { setGenerating(false); }
  };

  const adjustPoints = (idx, delta) => {
    setTeams(prev => prev.map((t, i) => i === idx ? { ...t, points: t.points + delta } : t));
    setWinner(null);
  };

  const setPointsDirect = (idx, val) => {
    const num = parseInt(val);
    if (isNaN(num)) return;
    setTeams(prev => prev.map((t, i) => i === idx ? { ...t, points: num } : t));
    setWinner(null);
  };

  const declareWinner = () => {
    if (teams.length === 0) return;
    const maxPoints = Math.max(...teams.map(t => t.points));
    const winners = teams.filter(t => t.points === maxPoints);
    if (winners.length === 1) {
      setWinner(winners[0].name);
      toast.success(`${winners[0].name} wins!`);
    } else {
      const tied = winners.map(w => w.name).join(' and ');
      setWinner(`Tie: ${tied}`);
      toast.success(`It's a tie between ${tied}!`);
    }
  };

  const breadcrumb = [
    { label: 'Teach Now', path: '/app/live-mode' },
    { label: classInfo?.name || 'Class', path: '/app/live-mode' },
    { label: 'Team Maker' }
  ];

  return (
    <Layout breadcrumb={breadcrumb}>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="teams-page">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-cyan-50 mb-4">
            <Shuffle className="w-7 h-7 text-cyan-600" />
          </div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900">Team Maker</h1>
          <p className="text-slate-500 mt-1 text-sm">{classInfo?.name} &middot; {students.length} students</p>
        </div>

        <Card className="bg-white border border-slate-200 rounded-xl shadow-soft">
          <CardContent className="p-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1.5 block">Number of Teams</label>
                <Input
                  type="number" min="2" max="10" value={teamCount}
                  onChange={(e) => setTeamCount(Math.min(10, Math.max(2, parseInt(e.target.value) || 2)))}
                  className="border-slate-200 rounded-xl"
                  data-testid="team-count-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1.5 block">Team Name Category</label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="border-slate-200 rounded-xl" data-testid="category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TEAM_NAME_CATEGORIES).map(([key, cat]) => (
                      <SelectItem key={key} value={key}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleGenerateTeams}
                  disabled={generating || loading || students.length === 0}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl h-10"
                  data-testid="generate-teams-btn"
                >
                  {generating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Shuffle className="w-4 h-4 mr-2" />}
                  {teams.length > 0 ? 'Reshuffle' : 'Generate'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Winner banner */}
        {winner && (
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-2xl p-6 text-center animate-fade-in" data-testid="winner-banner">
            <Trophy className="w-12 h-12 text-amber-500 mx-auto mb-2" />
            <h2 className="text-2xl font-heading font-bold text-amber-800">{winner}</h2>
            <p className="text-amber-600 text-sm mt-1">Winner!</p>
          </div>
        )}

        {/* Teams grid */}
        {teams.length > 0 && (
          <>
            <div className="grid md:grid-cols-2 gap-4">
              {teams.map((team, index) => {
                const colors = TEAM_COLORS[index % TEAM_COLORS.length];
                return (
                  <Card
                    key={index}
                    className={`${colors.bg} border-2 ${colors.border} rounded-xl animate-fade-in overflow-hidden`}
                    style={{ animationDelay: `${index * 80}ms` }}
                    data-testid={`team-${index}`}
                  >
                    <CardContent className="p-0">
                      {/* Team header */}
                      <div className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Sparkles className={`w-5 h-5 ${colors.accent}`} />
                          <h3 className={`font-heading font-bold text-lg ${colors.text}`} data-testid={`team-name-${index}`}>
                            {team.name}
                          </h3>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors.badge} ${colors.text}`}>
                            {team.members.length}
                          </span>
                        </div>
                        {/* Points control */}
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => adjustPoints(index, -1)}
                            className={`w-7 h-7 rounded-lg border ${colors.border} ${colors.bg} hover:opacity-80 flex items-center justify-center transition-all`}
                            data-testid={`points-minus-${index}`}
                          >
                            <Minus className="w-3.5 h-3.5" />
                          </button>
                          <input
                            type="number"
                            value={team.points}
                            onChange={(e) => setPointsDirect(index, e.target.value)}
                            className={`w-12 h-7 text-center rounded-lg border ${colors.border} bg-white text-sm font-bold ${colors.text} focus:outline-none focus:ring-2 focus:ring-teal-200`}
                            data-testid={`points-input-${index}`}
                          />
                          <button
                            onClick={() => adjustPoints(index, 1)}
                            className={`w-7 h-7 rounded-lg border ${colors.border} ${colors.bg} hover:opacity-80 flex items-center justify-center transition-all`}
                            data-testid={`points-plus-${index}`}
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      {/* Members */}
                      <div className="px-4 pb-4">
                        <ul className="space-y-1.5">
                          {team.members.map((member, i) => (
                            <li key={i} className="flex items-center gap-2 py-1">
                              <div className={`w-6 h-6 rounded-full ${colors.badge} flex items-center justify-center text-xs font-bold ${colors.text}`}>
                                {member.charAt(0)}
                              </div>
                              <span className={`text-sm ${colors.text}`}>{member}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Declare Winner */}
            <div className="text-center">
              <Button
                onClick={declareWinner}
                className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl h-12 px-8 text-base"
                data-testid="declare-winner-btn"
              >
                <Trophy className="w-5 h-5 mr-2" />Declare Winner
              </Button>
            </div>
          </>
        )}

        {students.length === 0 && !loading && (
          <Card className="bg-white border border-dashed border-slate-300 shadow-none">
            <CardContent className="py-12 text-center">
              <Users className="w-10 h-10 text-slate-200 mx-auto mb-3" />
              <h3 className="text-base font-medium text-slate-700 mb-1">No students in this class</h3>
              <p className="text-sm text-slate-400">Add students first to create teams</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};
